import { isMatch } from 'lodash/fp';
import { Component } from 'react';
import { KeyValue } from '@mews/enums';
import { ShortcutKey } from '../../modules/shortcuts/constants';

type KeyboardEventTarget = HTMLElement | Window | null;
export interface KeyboardEventHandlerProps {
    subscribeKeyEvents: (keyMap: KeyboardEventHandlerKeyMap, element: KeyboardEventTarget) => void;
    unsubscribeKeyEvents: () => void;
}

export type WithKeyboardEventHandler = KeyboardEventHandlerProps;

type HandlerNames = 'onEvent';

type KeyboardEventHandlerKeyOptions = {
    [key in HandlerNames]?: () => void;
};

export type KeyboardEventHandlerKeyMap =
    | {
          [key in KeyValue | ShortcutKey]?: KeyboardEventHandlerKeyOptions;
      }
    | undefined;

/** @deprecated Use useShortcuts hook instead */
export default <P extends KeyboardEventHandlerProps>(WrappedComponent: React.ComponentType<P>) =>
    class ComponentWithKeyboardEventHandler extends Component<Omit<P, keyof KeyboardEventHandlerProps>> {
        private element: KeyboardEventTarget = window;
        private keyMap: KeyboardEventHandlerKeyMap = undefined;

        subscribe = (keyMap: KeyboardEventHandlerKeyMap, element: KeyboardEventTarget = window) => {
            if (element) {
                this.element = element;
                this.keyMap = keyMap;
                this.element.addEventListener('keyup', this.handleKeyUp);
            }
        };

        unsubscribe = () => {
            if (this.element) {
                this.element.removeEventListener('keyup', this.handleKeyUp);
            }
        };

        handleKeyUp = (event: Event) => {
            if (this.keyMap) {
                // @ts-expect-error
                const e = this.keyMap[event.code];

                if (e && isMatch(e.modifiers)(event)) {
                    event.preventDefault();
                    event.stopPropagation();

                    e.onEvent();
                }
            }
        };

        render() {
            const props = {
                ...this.props,
                subscribeKeyEvents: this.subscribe,
                unsubscribeKeyEvents: this.unsubscribe,
            };

            return <WrappedComponent {...(props as P)} />;
        }
    };
